import type { SasTokenResponse } from './sas';
import {
  collectFilePaths,
  collectFilePathsForHtml,
  getContentType,
} from '@/utils/files';
import { readFileBuffer } from '@/utils/fs';
import { withFileUploadRetry } from '@/utils/retry';
import { isVerboseMode } from '@/utils/verbose';
import { logger } from '@/utils/logger';

/**
 * Upload progress callback type
 */
type ProgressCallback = (
  uploadedFiles: number,
  totalFiles: number,
  currentFile: string
) => void;

/**
 * Azure storage client configured with SAS-based authentication
 */
export class AzureStorageClient {
  private readonly sasResponse: SasTokenResponse;

  constructor(sasResponse: SasTokenResponse) {
    this.sasResponse = sasResponse;
  }

  /**
   * Upload a single file using the SAS token response format
   */
  async uploadFile(filePath: string, blobPath: string): Promise<string> {
    // Pre-validate file type before attempting upload
    const allowedTypes = this.sasResponse.allowedFileTypes;
    const fileExtension = filePath.split('.').pop()?.toLowerCase() || '';

    if (allowedTypes.length > 0 && !allowedTypes.includes(fileExtension)) {
      throw new Error(
        `File type '${fileExtension}' not allowed. Allowed types: ${allowedTypes.join(', ')}`
      );
    }

    // Build the complete upload URL using the SAS response format
    const { containerUrl, blobPath: pathPrefix } = this.sasResponse;

    // Combine path prefix with blob path
    const fullBlobPath = `${pathPrefix}/${blobPath}`;

    // Build upload URL (containerUrl + path + SAS token)
    const uploadUrl = `${containerUrl}/${fullBlobPath}?${this.sasResponse.sasToken}`;

    // Read file data
    const data = await readFileBuffer(filePath);

    // Detect content type
    const contentType = getContentType(filePath);

    // Validate file size
    if (data.length > this.sasResponse.maxSize) {
      const sizeMB = Math.round(data.length / (1024 * 1024));
      const maxSizeMB = Math.round(this.sasResponse.maxSize / (1024 * 1024));
      throw new Error(`File too large: ${sizeMB}MB (max: ${maxSizeMB}MB)`);
    }

    // Prepare headers based on example upload format
    const headers: Record<string, string> = {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': contentType,
      'Content-Length': data.length.toString(),
    };

    // Upload file using PUT request
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers,
      body: data,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(
        `TestDino platform upload failed: ${response.status} ${errorText}`
      );
    }

    // Return the public URL (without SAS token for public access)
    return `${this.sasResponse.containerUrl}/${fullBlobPath}`;
  }
}

/**
 * Service to upload files to Azure Blob Storage using SAS credentials with optimal concurrency
 */
export class AzureUploadService {
  private readonly maxConcurrentUploads = 5; // Optimal for most scenarios

  constructor(private readonly storageClient: AzureStorageClient) {}

  /**
   * Upload an entire directory of files with concurrent uploads and progress tracking
   * @param localDir Local directory path
   * @param prefix Additional prefix for blob paths (optional)
   * @param onProgress Progress callback (optional)
   * @param preFilteredPaths Optional pre-filtered file paths (if not provided, will collect all files)
   */
  async uploadDirectory(
    localDir: string,
    prefix: string = '',
    onProgress?: ProgressCallback,
    preFilteredPaths?: string[]
  ): Promise<string[]> {
    // Use provided file paths or collect all files recursively
    const filePaths = preFilteredPaths || (await collectFilePaths(localDir));

    const uploadedUrls: string[] = [];
    let uploadedCount = 0;

    // Upload files in batches for optimal performance
    const batches = this.createBatches(filePaths, this.maxConcurrentUploads);

    for (const batch of batches) {
      // Upload batch concurrently
      const batchPromises = batch.map(async filePath => {
        try {
          // Compute blob path: prefix + relative path from directory
          const relativePath = this.getRelativePath(filePath, localDir);
          const blobPath = prefix ? `${prefix}/${relativePath}` : relativePath;

          // Upload with retry logic
          const uploadUrl = await withFileUploadRetry(
            async () => this.storageClient.uploadFile(filePath, blobPath),
            filePath
          );

          uploadedUrls.push(uploadUrl);
          uploadedCount++;

          // Report progress
          if (onProgress) {
            onProgress(uploadedCount, filePaths.length, relativePath);
          }

          return uploadUrl;
        } catch (_error) {
          // Show warnings only for critical files or if verbose is enabled
          const isImportantFile =
            filePath.includes('index.html') || filePath.includes('.json');
          if (isImportantFile || isVerboseMode()) {
            const fileName = filePath.split('/').pop() || filePath;
            logger.warn(`Skipped: ${fileName}`);
          }
          return null;
        }
      });

      // Wait for current batch to complete before starting next
      const results = await Promise.all(batchPromises);

      // Filter out null results (skipped files)
      results.filter(url => url !== null) as string[];
      // Note: uploadedUrls is already populated in the map function above
    }

    return uploadedUrls;
  }

  /**
   * Get relative path from a local directory, ensuring proper path handling
   */
  private getRelativePath(filePath: string, localDir: string): string {
    // Normalize paths to handle different OS separators
    const normalizedFilePath = filePath.replace(/\\/g, '/');
    const normalizedLocalDir = localDir.replace(/\\/g, '/').replace(/\/$/, '');

    // Remove the local directory prefix to get relative path
    let relativePath = normalizedFilePath.replace(`${normalizedLocalDir}/`, '');

    // Handle edge case where local dir doesn't end with /
    if (relativePath === normalizedFilePath) {
      relativePath = normalizedFilePath.replace(normalizedLocalDir, '');
      if (relativePath.startsWith('/')) {
        relativePath = relativePath.substring(1);
      }
    }

    return relativePath;
  }

  /**
   * Create batches of files for concurrent upload
   */
  private createBatches<T>(items: T[], batchSize: number): T[][] {
    const batches: T[][] = [];
    for (let i = 0; i < items.length; i += batchSize) {
      batches.push(items.slice(i, i + batchSize));
    }
    return batches;
  }

  /**
   * Upload with progress reporting and detailed feedback
   */
  async uploadDirectoryWithProgress(
    localDir: string,
    prefix: string = ''
  ): Promise<string[]> {
    return this.uploadDirectory(localDir, prefix);
  }

  /**
   * Upload HTML directory with filtering based on upload configuration
   */
  async uploadHtmlDirectoryWithProgress(
    localDir: string,
    prefix: string = '',
    config?: {
      uploadImages: boolean;
      uploadVideos: boolean;
      uploadHtml: boolean;
    }
  ): Promise<string[]> {
    const filePaths = await collectFilePathsForHtml(localDir, true, config);

    const uploadedUrls = await this.uploadDirectory(
      localDir,
      prefix,
      undefined,
      filePaths
    );

    return uploadedUrls;
  }
}
